<template>
  <v-container fluid class="black-text">
    <v-row class="mx-1" align="center">
      <v-col>
        <v-btn
          small
          rounded
          color="primary"
          @click="$router.push({ name: 'reports' })"
        >
          <v-icon>mdi-chevron-left</v-icon>
          {{ $t('reports.reportHeader.backToReports') }}
        </v-btn>
      </v-col>
      <v-spacer />
      <v-col class="d-flex flex-column justify-end align-end">
        <v-row class="d-flex align-center gap-1">
          <InfoTooltip :text="$t('reports.reportHeader.pageFormatTooltip')" size="x-large"/>
          <v-select
            :items="pageFormatList"
            v-model="pageFormat"
            item-text="text"
            item-value="value"
            :label="$t('reports.reportHeader.selectPdfView')"
            class="mr-6 ml-2"
            style="width: 7rem;"
            />
        <v-btn
          @click="handleClickGeneratePdf(pageFormat)"
          small
          rounded
          color="yellow"
        >
          {{ $t('reports.reportHeader.generatePdf') }}
        </v-btn>
        </v-row>
        <v-checkbox
          v-if="haveMultipleParts"
          v-model="showParts"
          :label="contract.isMultiCurrency ? $t('reports.showParts') : $t('reports.showSites')"
          hide-details
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <ReportHeader
          :item="item"
          :contract="contract"
          @save="handleSave"
        />
      </v-col>
      <v-col cols="12" md="6">
        <ReportFinances
          :report="item.report"
          :chosen-currency="chosenCurrency"
          :currencies="contract.currencies"
          :show-parts="showParts"
          :parts="parts"
        />
      </v-col>
      <v-col cols="12" md="6">
        <ReportNotes
          :comment="item.comment"
          :chosen-currency="chosenCurrency"
          @save="handleSave"
        />
      </v-col>
      <v-col cols="12" md="12">
        <ReportTopics
          :report="item.report"
          :chosen-currency="chosenCurrency"
          :currencies="contract.currencies"
          :show-parts="showParts"
          :parts="parts"
        />
      </v-col>
      <v-col cols="12" md="12" :lg="barChartData.length > 4 ? 12 : 6">
        <ReportChartsDoughnut
          :report="item.report"
          :chosen-currency="chosenCurrency"
          :currencies="contract.currencies"
        />
      </v-col>
      <v-col v-if="barChartData.length > 0 && barChartData.length < 4" cols="12" lg="6" md="12">
        <BarChart
          :label="barChartLabel"
          :data="barChartData"
          :chosen-currency="chosenCurrency"
        />
      </v-col>
      <v-col v-if="barChartData.length >= 5" cols="12" lg="12" md="12">
        <BarChart
          :label="barChartLabel"
          :data="barChartData"
          :chosen-currency="chosenCurrency"
        />
      </v-col>
      <template v-for="type in openVariationsList">
        <v-col :key="`${type}-variations`" cols="12">
          <v-card>
            <VariationReportTable
              :items="report.variations.filter(i => i.type === type)"
              :type="type"
              :chosenCurrency="chosenCurrency"
              :currencies="contract.currencies"
              :show-parts="showParts"
              :parts="parts"
              :isMultiCurrency="contract.isMultiCurrency"
            />
          </v-card>
        </v-col>
        <v-col :key="`${type}-finalised`" cols="12">
          <v-card>
            <VariationReportTable
              :items="report.finalisedVariations ? report.finalisedVariations.filter(i => i.type === type) : []"
              :type="type"
              :labelsKey="'reports.finalisedVariations'"
              :chosenCurrency="chosenCurrency"
              :currencies="contract.currencies"
              :show-parts="showParts"
              :parts="parts"
              :isMultiCurrency="contract.isMultiCurrency"
            />
          </v-card>
        </v-col>
      </template>
    </v-row>
    <ReportPdfTemplate :contract="contract" :reports="reports" ref="pdfTemplateRef" :showParts="showParts" :pageFormat="pageFormat"/>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { updateReport } from '../services'
import { DEFAULT_REPORT } from '@/helpers'

export default {
  name: 'ReportView',
  props: {
    contract: Object,
    reports: {
      type: Array,
      default: () => ([])
    }
  },
  data () {
    return {
      barChartLabel: `${this.$t('reports.topics.riskCauses')}`,
      openVariationsList: ['variation', 'claim', 'amendment'],
      finalizedVariationsList: ['variation', 'claim', 'amendment'],
      notesEdit: false,
      showParts: false,
      menu: false,
      pageFormatList: [
        { text: this.$t('reports.reportHeader.expandedPdfView'), value: 'expanded' },
        { text: this.$t('reports.reportHeader.compactPdfView'), value: 'compact' }
      ],
      pageFormat: 'expanded'
    }
  },
  computed: {
    parts () {
      return this.item.report.parts.map(part => {
        return {
          ...(this.contract.parts.find(i => i.id === part.id) || {}),
          ...part
        }
      })
    },
    haveMultipleParts () {
      return this.contract.parts.length > 1
    },
    item () {
      const item = this.reports.find(i => Number(this.$route.params.reportId) === i.id) || DEFAULT_REPORT
      return item
    },
    report () {
      return this.item.report
    },
    barChartData () {
      const categoryValues = {}
      if (this.report && this.report.finalisedVariations && this.report.finalisedVariations.length > 0) {
        this.report.finalisedVariations
          .filter(variation => variation.category && variation.agreedValueCents.sum !== 0)
          .forEach(variation => {
            if (variation.category in categoryValues) {
              categoryValues[variation.category] += variation.agreedValueCents.sum
            } else {
              categoryValues[variation.category] = variation.agreedValueCents.sum
            }
          })
      }

      const filteredData = Object.keys(categoryValues)
        .map(category => ({
          value: categoryValues[category],
          category
        }))
        .sort((a, b) => b.value - a.value)

      return filteredData
    },
    ...mapState({
      currencies: state => state.currencies
    }),
    chosenCurrency () {
      return this.contract.chosenCurrency
    }
  },
  methods: {
    async handleSave (data) {
      await updateReport(this.contract.id, this.item.id, data)
      this.$emit('FETCH_DATA')
    },
    handleClickGeneratePdf (pageFormat) {
      this.pageFormat = pageFormat
      this.$refs.pdfTemplateRef.$refs.html2Pdf.generatePdf()
    }
  }
}
</script>

<style lang="css">
  .black-text .v-card__text, .black-text .v-card__title {
    color: black !important;
  }
</style>
